import { Brain, MapPin, Building2, Users, Zap, Sparkles } from "lucide-react";
import type { LivePreview } from "../types";

interface DiagnosisPreviewProps {
  preview: LivePreview;
}

export default function DiagnosisPreview({ preview }: DiagnosisPreviewProps) {
  const posLabel =
    preview.positionsMin !== "" || preview.positionsMax !== ""
      ? `${preview.positionsMin !== "" ? preview.positionsMin : "?"} – ${preview.positionsMax !== "" ? preview.positionsMax : "?"} posições`
      : "— posições";

  return (
    <div
      className="w-full h-full flex flex-col items-center p-6 gap-4"
      style={{ animation: "aiPreviewFade 0.4s ease" }}
    >
      <div
        className="relative w-full max-w-2xl rounded-3xl overflow-hidden shadow-xl"
        style={{ flex: "1 1 0%" }}
      >
        <img
          src="https://d1y4va1nna2r1p.cloudfront.net/spaces/e1c6b9c8-67af-4ebc-a5e4-855cc42dfd8c.jpeg"
          alt="Escritório"
          className="w-full h-full object-cover"
        />

        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, transparent 40%, transparent 55%, rgba(0,0,0,0.55) 100%)",
          }}
        />

        {preview.dealImported && preview.companyName && (
          <div
            className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold"
            style={{
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(8px)",
              color: "#1A1814",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              animation: "aiStepEnter 0.4s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            <div
              className="w-6 h-6 rounded-lg flex items-center justify-center"
              style={{ background: "#FFD900" }}
            >
              <Building2 className="w-3.5 h-3.5" style={{ color: "#1A1814" }} />
            </div>
            {preview.companyName}
          </div>
        )}

        {preview.dealImported && preview.targetCity && (
          <div
            className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold"
            style={{
              background: "linear-gradient(135deg, #FFD900, #FFE94D)",
              boxShadow: "0 4px 16px rgba(255,217,0,0.3)",
              color: "#1A1814",
              animation: "aiStepEnter 0.4s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            <MapPin className="w-4 h-4" />
            {preview.targetCity}
          </div>
        )}

        <div
          className="absolute bottom-4 left-4 flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold"
          style={{
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(8px)",
            color: "#1A1814",
          }}
        >
          <Users className="w-4 h-4" />
          {posLabel}
        </div>

        {preview.dealImported && (
          <div
            className="absolute bottom-4 right-4 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
            style={{
              background:
                "linear-gradient(135deg, rgba(124,58,237,0.9), rgba(139,92,246,0.85))",
              backdropFilter: "blur(8px)",
              color: "white",
              animation: "aiStepEnter 0.5s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            <Zap className="w-3.5 h-3.5" />
            AI Insights ativos
          </div>
        )}
      </div>

      {preview.dealImported && (preview.dealInsights ?? []).length > 0 && (
        <div
          className="rounded-2xl overflow-hidden flex-shrink-0 w-full max-w-2xl"
          style={{
            background:
              "linear-gradient(135deg, rgba(124,58,237,0.04) 0%, rgba(139,92,246,0.02) 100%)",
            border: "1px solid rgba(167,139,250,0.2)",
            animation: "aiStepEnter 0.5s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          <div
            className="px-3.5 py-2.5 flex items-center gap-2"
            style={{
              background:
                "linear-gradient(90deg, rgba(124,58,237,0.08), rgba(139,92,246,0.03))",
              borderBottom: "1px solid rgba(167,139,250,0.1)",
            }}
          >
            <div
              className="w-5 h-5 rounded-md flex items-center justify-center"
              style={{ background: "#7C3AED" }}
            >
              <Brain className="w-3 h-3 text-white" />
            </div>
            <span
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ color: "#7C3AED" }}
            >
              Woba AI — Insights do Deal
            </span>
          </div>

          <div className="px-3.5 py-3 space-y-2">
            {(preview.dealInsights ?? []).map((text: string, i: number) => (
              <div key={i} className="flex items-start gap-2">
                <div
                  className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: "rgba(167,139,250,0.1)" }}
                >
                  <Sparkles
                    className="w-2.5 h-2.5"
                    style={{ color: "#7C3AED" }}
                  />
                </div>
                <p
                  className="text-[11px] leading-relaxed"
                  style={{ color: "#43403D" }}
                >
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
